import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import api, { setTokens, clearTokens, getToken } from '@/store/api';
import { CATEGORIES, FREQUENCIES, TIME_OPTIONS, PHONE_PREFIXES, FREQ_LABELS } from '@/lib/constants';
import { fmt, calculateCuotaBreakdown } from '@/lib/formatters';

const AppContext = createContext(null);

const INITIAL_USER = {
  nombre: '',
  apellido: '',
  cedula: '',
  telefono: '',
  email: '',
  avatar: 'https://images.unsplash.com/photo-1745501231703-099a14bdcac0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxoYXBweSUyMGRvbWluaWNhbiUyMHBlcnNvbiUyMHBvcnRyYWl0JTIwbmF0dXJhbCUyMGxpZ2h0fGVufDB8fHx8MTc3MjgxMjk2Mnww&ixlib=rb-4.1.0&q=85',
};

export function AppProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(INITIAL_USER);
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [plan, setPlan] = useState({ amount: 0, frequency: 'mensual', months: 12 });
  const [savingsModality, setSavingsModality] = useState(null);
  const [kompaClubChoice, setKompaClubChoice] = useState(null);
  const [leads, setLeads] = useState([{ nombre: '', telefonoPrefix: '0412', telefonoNum: '', email: '' }]);
  const [kyc, setKyc] = useState({
    phoneValidated: false, emailValidated: false, cedulaUploaded: false,
    rifUploaded: false, termsAccepted: false, termsDate: null, kycCompleted: false,
  });
  const [savingsSubModality, setSavingsSubModality] = useState(null);
  const [goals, setGoals] = useState([]);
  const [isAddingGoal, setIsAddingGoal] = useState(false);
  const [goalEditHistory, setGoalEditHistory] = useState({});
  const [bankAccounts, setBankAccounts] = useState([]);
  const [planTemplates, setPlanTemplates] = useState([]);

  // ── Auto-login on mount ──
  useEffect(() => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    Promise.all([api('/api/profile'), api('/api/goals'), api('/api/kyc'), api('/api/bank-accounts'), api('/api/plan-templates').catch(() => [])])
      .then(([profile, goalsData, kycData, bankData, templates]) => {
        setUser({ ...INITIAL_USER, ...(profile || {}) });
        setGoals(Array.isArray(goalsData) ? goalsData : []);
        setKyc(prev => ({ ...prev, ...(kycData || {}) }));
        setBankAccounts(Array.isArray(bankData) ? bankData : []);
        setPlanTemplates(Array.isArray(templates) ? templates : []);
        setIsAuthenticated(true);
        setCurrentStep(Array.isArray(goalsData) && goalsData.length > 0 ? 11 : 1);
      })
      .catch(() => { try { clearTokens(); } catch { /* ignore */ } })
      .finally(() => setLoading(false));
  }, []);

  // ── KYC sync to API ──
  const kycMounted = useRef(false);
  useEffect(() => {
    if (!kycMounted.current) { kycMounted.current = true; return; }
    if (!isAuthenticated || !getToken()) return;
    api('/api/kyc', {
      method: 'PUT',
      body: JSON.stringify({
        phone_validated: kyc.phoneValidated, email_validated: kyc.emailValidated,
        cedula_uploaded: kyc.cedulaUploaded, rif_uploaded: kyc.rifUploaded,
        terms_accepted: kyc.termsAccepted, kyc_completed: kyc.kycCompleted,
      }),
    }).catch(() => {});
  }, [kyc, isAuthenticated]);

  // ── Auth ──
  const login = useCallback(async (email, password) => {
    setAuthError(null);

    // STAGE 1 — Authenticate. ONLY auth failures here should ever surface
    // as a "login error" toast. A successful POST /api/auth/login means
    // the user's credentials are valid; any subsequent data-fetch error
    // belongs to the dashboard, not the login screen.
    let authRes;
    try {
      authRes = await api('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) });
    } catch (e) {
      const raw = (e?.message || '').trim();
      // Map generic fallback messages to actionable, credential-focused copy
      // so users immediately know what to try (re-type password) instead of
      // suspecting an outage.
      let friendly = raw;
      if (!raw || /sesion expirada/i.test(raw) || /sesi.n inv.lida/i.test(raw)) {
        friendly = 'Correo o contraseña incorrectos. Verifica e intenta nuevamente.';
      } else if (/error del servidor/i.test(raw)) {
        friendly = 'No pudimos conectar con el servidor. Intenta nuevamente en unos segundos.';
      } else if (/sin conexion/i.test(raw)) {
        friendly = 'Sin conexión a internet. Verifica tu red e intenta nuevamente.';
      }
      setAuthError(friendly);
      // Classify so the UI can decide whether to count this against the
      // "stuck user" heuristic (we only count credential failures, never
      // network/server outages — those aren't the user's fault).
      const isCredentialError = /incorrect|credenciales|password/i.test(friendly);
      return { ok: false, reason: isCredentialError ? 'credentials' : 'network' };
    }

    // STAGE 2 — Credentials accepted. Mark authenticated immediately so
    // the user is NEVER stuck on the login page because of a downstream
    // 5xx on /api/goals or /api/kyc.
    setTokens(authRes?.access_token, authRes?.refresh_token);
    setUser({ ...INITIAL_USER, ...(authRes?.user || {}) });

    // STAGE 3 — Best-effort hydrate of dashboard data. Per-call resilience
    // so a single failing endpoint doesn't blank the rest.
    const [goalsData, kycData, bankData, templates] = await Promise.all([
      api('/api/goals').catch(() => []),
      api('/api/kyc').catch(() => null),
      api('/api/bank-accounts').catch(() => []),
      api('/api/plan-templates').catch(() => []),
    ]);
    setGoals(Array.isArray(goalsData) ? goalsData : []);
    if (kycData) setKyc(prev => ({ ...prev, ...kycData }));
    setBankAccounts(Array.isArray(bankData) ? bankData : []);
    setPlanTemplates(Array.isArray(templates) ? templates : []);
    setIsAuthenticated(true);
    setCurrentStep(Array.isArray(goalsData) && goalsData.length > 0 ? 11 : 1);
    return { ok: true };
  }, []);

  const signup = useCallback(async (userData) => {
    setAuthError(null);
    try {
      const res = await api('/api/auth/signup', {
        method: 'POST',
        body: JSON.stringify({
          nombre: userData.nombre, apellido: userData.apellido,
          cedula: userData.cedula, telefono: userData.telefono,
          email: userData.email, password: userData.password,
        }),
      });
      setTokens(res.access_token, res.refresh_token);
      setUser({ ...INITIAL_USER, ...res.user, avatar: userData.avatar || INITIAL_USER.avatar });
      setIsAuthenticated(true);
      setCurrentStep(1);
    } catch (e) {
      // Provide actionable error message for common signup failures
      const msg = e.message || '';
      if (msg.includes('Datos inv') || msg === 'Error del servidor') {
        setAuthError('Este correo podria estar registrado. Intenta con otro o inicia sesion.');
      } else {
        setAuthError(msg);
      }
    }
  }, []);

  const logout = useCallback(() => {
    clearTokens();
    setIsAuthenticated(false);
    setUser(INITIAL_USER);
    setCurrentStep(0);
    setSelectedCategory(null);
    setPlan({ amount: 0, frequency: 'mensual', months: 12 });
    setSavingsModality(null);
    setKompaClubChoice(null);
    setLeads([{ nombre: '', telefonoPrefix: '0412', telefonoNum: '', email: '' }]);
    setKyc({ phoneValidated: false, emailValidated: false, cedulaUploaded: false, rifUploaded: false, termsAccepted: false, termsDate: null, kycCompleted: false });
    setSavingsSubModality(null);
    setGoals([]);
    setIsAddingGoal(false);
    setGoalEditHistory({});
    setBankAccounts([]);
  }, []);

  const selectCategory = useCallback((categoryId) => {
    setSelectedCategory(prev => prev === categoryId ? null : categoryId);
  }, []);

  const calculatePayment = useCallback(() => {
    // CANONICAL: fold the service fee in so the figure matches PlanCalculator,
    // EditGoalView and PaymentSummary (single source of truth).
    const syntheticGoal = {
      totalAmount: plan.amount,
      months: plan.months,
      frequencyId: plan.frequency,
      modality: savingsModality || 'total',
      subModality: savingsSubModality || 'individual',
      planCode: 'kompa_plus',
    };
    return calculateCuotaBreakdown(syntheticGoal, 1, null).cuota;
  }, [plan, savingsModality, savingsSubModality]);

  // ── Goals (API-backed) ──
  const addGoal = useCallback(async () => {
    const cat = CATEGORIES.find(c => c.id === selectedCategory);
    const now = new Date();
    const endDate = new Date(now);
    endDate.setMonth(endDate.getMonth() + plan.months);
    const goalData = {
      category: selectedCategory || 'motos',
      label: cat?.label || 'Meta',
      icon: cat?.icon || 'Bike',
      total_amount: plan.amount,
      frequency: FREQ_LABELS[plan.frequency] || 'Mensual',
      frequency_id: plan.frequency,
      months: plan.months,
      modality: savingsModality || 'total',
      sub_modality: savingsSubModality || (savingsModality === 'club' ? kompaClubChoice : 'individual'),
      start_date: now.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }),
      estimated_end: endDate.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }),
    };
    try {
      const newGoal = await api('/api/goals', { method: 'POST', body: JSON.stringify(goalData) });
      setGoals(prev => [...prev, newGoal]);
    } catch (e) {
      // Fallback to local if API fails
      const isKC = goalData.modality === 'club' || goalData.sub_modality === 'grupal';
      const fallback = {
        id: String(Date.now()), ...goalData,
        totalAmount: goalData.total_amount, savedAmount: Math.round(goalData.total_amount * 0.035 * 100) / 100,
        frequencyId: goalData.frequency_id, subModality: goalData.sub_modality,
        startDate: goalData.start_date, estimatedEnd: goalData.estimated_end, payments: [],
        status: 'active', grossPaid: 0,
        planName: isKC ? 'Plan Acelerado' : 'Plan KOMPA+',
        planCode: isKC ? 'acelerado' : 'kompa_plus',
        bonus: isKC ? { hasBonus: false } : {
          hasBonus: true, bonusPct: 0.15,
          maxBonusEur: Math.round(goalData.total_amount * 0.15 * 100) / 100,
          earnedBonusEur: 0, progressPct: 0,
        },
      };
      setGoals(prev => [...prev, fallback]);
    }
  }, [selectedCategory, plan, savingsModality, savingsSubModality, kompaClubChoice]);

  const updateGoal = useCallback(async (goalId, updates) => {
    setGoals(prev => prev.map(g => g.id === goalId ? { ...g, ...updates } : g));
    setGoalEditHistory(prev => ({ ...prev, [goalId]: true }));
    try {
      const apiUpdates = {};
      if (updates.totalAmount !== undefined) apiUpdates.total_amount = updates.totalAmount;
      if (updates.frequency) apiUpdates.frequency = updates.frequency;
      if (updates.frequencyId) apiUpdates.frequency_id = updates.frequencyId;
      if (updates.months) apiUpdates.months = updates.months;
      if (updates.estimatedEnd) apiUpdates.estimated_end = updates.estimatedEnd;
      if (updates.category) apiUpdates.category = updates.category;
      if (updates.label) apiUpdates.label = updates.label;
      if (updates.icon) apiUpdates.icon = updates.icon;
      await api(`/api/goals/${goalId}`, { method: 'PUT', body: JSON.stringify(apiUpdates) });
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[KOMPA] goal update API failed, keeping optimistic state:', e);
      }
    }
  }, []);

  // ── Bank Accounts (API-backed) ──
  const addBankAccount = useCallback(async (account) => {
    const tempId = String(Date.now());
    setBankAccounts(prev => [...prev, { ...account, id: tempId }]);
    try {
      const newAcc = await api('/api/bank-accounts', { method: 'POST', body: JSON.stringify(account) });
      setBankAccounts(prev => prev.map(a => a.id === tempId ? newAcc : a));
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[KOMPA] bank account API failed, keeping temp entry:', e);
      }
    }
  }, []);

  const refreshGoals = useCallback(async () => {
    try {
      const goalsData = await api('/api/goals');
      setGoals(goalsData);
      return goalsData;
    } catch (e) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[KOMPA] refreshGoals failed:', e);
      }
      return null;
    }
  }, []);

  const value = {
    loading, authError, setAuthError,
    isAuthenticated, user, currentStep, selectedCategory, plan,
    savingsModality, kompaClubChoice, leads,
    categories: CATEGORIES, frequencies: FREQUENCIES,
    timeOptions: TIME_OPTIONS, phonePrefixes: PHONE_PREFIXES,
    kyc, login, signup, logout, setUser, setCurrentStep,
    selectCategory, setPlan, setSavingsModality, setKompaClubChoice,
    setKyc, setLeads, calculatePayment,
    savingsSubModality, setSavingsSubModality,
    goals, addGoal, updateGoal,
    isAddingGoal, setIsAddingGoal, goalEditHistory,
    bankAccounts, addBankAccount,
    planTemplates,
    refreshGoals,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#fbb040] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm text-gray-400 font-light">Cargando...</p>
        </div>
      </div>
    );
  }

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used within AppProvider');
  return context;
}
